import React from "react";
import ReactDOM from "react-dom/client";
import {
  ArrowDown,
  ArrowRight,
  AtSign,
  BarChart3,
  CalendarClock,
  Check,
  CircleDot,
  Clock3,
  FileText,
  Facebook,
  Hash,
  Image as ImageIcon,
  Instagram,
  Layers3,
  Linkedin,
  Mail,
  MessageSquareText,
  PenLine,
  Scissors,
  Send,
  ShieldCheck,
  Sparkles,
  Type,
  Twitter,
  UsersRound,
  Youtube,
  Gift,
  Link2,
  BadgeDollarSign
} from "lucide-react";
import "./styles.css";

const funnelOutputs = [
  {
    icon: Linkedin,
    channel: "LinkedIn",
    detail: "Long-form rewrite, link moved to first comment",
    time: "08:30"
  },
  {
    icon: Twitter,
    channel: "X",
    detail: "Split into a 3-post thread, 280 characters each",
    time: "08:35"
  },
  {
    icon: Instagram,
    channel: "Instagram",
    detail: "4:5 crop, caption trimmed, 12 tags appended",
    time: "09:10"
  },
  {
    icon: Facebook,
    channel: "Facebook",
    detail: "Link preview kept, CTA line added",
    time: "11:00"
  },
  {
    icon: Youtube,
    channel: "YouTube",
    detail: "Shorts description with chapters and tags",
    time: "14:20"
  }
];

const funnelOps = [
  { icon: Type, label: "Tone per channel" },
  { icon: Scissors, label: "Length and threading" },
  { icon: ImageIcon, label: "Crops and aspect ratios" },
  { icon: Hash, label: "Tags and mentions" },
  { icon: Clock3, label: "Slotted into the calendar" }
];

const workflowSteps = [
  {
    icon: CalendarClock,
    title: "Plan windows",
    copy: "Shape daily publishing around channels, approvals, and time-sensitive moments."
  },
  {
    icon: UsersRound,
    title: "Coordinate teams",
    copy: "Keep creators, clients, and operators aligned without moving work across tabs."
  },
  {
    icon: BarChart3,
    title: "Read the signal",
    copy: "Track what landed, what stalled, and where the next post should go."
  }
];

const metrics = [
  ["12", "channels queued"],
  ["4.8h", "approval saved"],
  ["93%", "on-time posts"]
];

const legalUpdated = "September 1, 2026";

const APP_AUTH = "https://app.postunite.com/auth";
const APP_AFFILIATES = "https://app.postunite.com/affiliates";
const REF_KEY = "postunite_ref";
const REF_MAX_AGE = 90 * 24 * 60 * 60;
const REF_PATTERN = /^[A-Za-z0-9_-]{3,64}$/;

function persistRef(code: string) {
  localStorage.setItem(REF_KEY, code);
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const domain =
    window.location.hostname === "postunite.com" ||
    window.location.hostname.endsWith(".postunite.com")
      ? "; Domain=postunite.com"
      : "";
  document.cookie = `${REF_KEY}=${encodeURIComponent(code)}; max-age=${REF_MAX_AGE}; path=/${domain}${secure}; SameSite=Lax`;
}

function readStoredRef() {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get("ref");
  if (fromUrl && REF_PATTERN.test(fromUrl)) {
    persistRef(fromUrl);
    return fromUrl;
  }
  const match = document.cookie.match(/(?:^|; )postunite_ref=([^;]*)/);
  if (match) {
    const value = decodeURIComponent(match[1]);
    if (REF_PATTERN.test(value)) {
      localStorage.setItem(REF_KEY, value);
      return value;
    }
  }
  const stored = localStorage.getItem(REF_KEY);
  return stored && REF_PATTERN.test(stored) ? stored : null;
}

function authUrl(ref: string | null) {
  if (!ref) {
    return APP_AUTH;
  }
  return `${APP_AUTH}?ref=${encodeURIComponent(ref)}`;
}

const AffiliateRefContext = React.createContext<string | null>(null);

function useAffiliateRef() {
  return React.useContext(AffiliateRefContext);
}


function App() {
  const [path, setPath] = React.useState(() => window.location.pathname);
  const [ref, setRef] = React.useState<string | null>(() => readStoredRef());

  React.useEffect(() => {
    const handleRoute = () => setPath(window.location.pathname);
    window.addEventListener("popstate", handleRoute);
    return () => window.removeEventListener("popstate", handleRoute);
  }, []);

  React.useEffect(() => {
    setRef(readStoredRef());
  }, [path]);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    document.title =
      path === "/privacy"
        ? "Privacy Policy | PostUnite"
        : path === "/terms"
          ? "Terms of Service | PostUnite"
          : path === "/affiliates"
            ? "Affiliates | PostUnite"
            : "PostUnite | One post in, every channel out";
  }, [path]);

  const shell = (page: React.ReactNode) => (
    <AffiliateRefContext.Provider value={ref}>
      <Header />
      {page}
      <Footer />
    </AffiliateRefContext.Provider>
  );

  if (path === "/privacy") {
    return shell(<PrivacyPage />);
  }

  if (path === "/terms") {
    return shell(<TermsPage />);
  }

  if (path === "/affiliates") {
    return shell(<AffiliatesPage />);
  }

  return shell(<LandingPage />);
}

function Header() {
  return (
    <header className="site-header">
      <a className="brand" href="/" aria-label="PostUnite home">
        <span className="brand-mark" aria-hidden="true">
          <Layers3 size={18} strokeWidth={2.25} />
        </span>
        PostUnite
      </a>
      <nav aria-label="Primary navigation">
        <a href="/#product">Product</a>
        <a href="/#workflow">Workflow</a>
        <a href="/affiliates">Affiliates</a>
        <a href="/#waitlist">Waitlist</a>
      </nav>
      <a className="header-cta" href="/#waitlist">
        Join the waitlist
      </a>
    </header>
  );
}

function LandingPage() {
  const ref = useAffiliateRef();

  return (
      <main id="top">
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero-wash" aria-hidden="true" />
          <div className="hero-grid" aria-hidden="true" />
          <div className="hero-content">
            <div className="hero-copy reveal">
              <p className="hero-eyebrow">One post in. Every channel out.</p>
              <h1 id="hero-title">Write it once. PostUnite publishes it everywhere.</h1>
              <p className="hero-subtitle">
                Drop in a single draft. PostUnite reshapes it for each network — tone, length, crop, tags, timing — and schedules the whole set in one pass. We are opening to teams soon.
              </p>
              <HeroActions />
            </div>

            <FunnelStage />
          </div>
          <a className="scroll-cue" href="/#product" aria-label="Scroll to product section">
            <ArrowDown size={18} />
          </a>
        </section>

        <section className="section rhythm" id="product" aria-labelledby="rhythm-title">
          <div className="section-kicker">Daily rhythm</div>
          <div className="section-heading">
            <h2 id="rhythm-title">Built for the daily publishing rhythm</h2>
            <p>
              PostUnite turns scattered calendars, approvals, drafts, and performance notes into one precise operating surface.
            </p>
          </div>
          <div className="rhythm-layout">
            <div className="rhythm-copy">
              <div className="timeline-line" aria-hidden="true" />
              {workflowSteps.map((step) => {
                const Icon = step.icon;
                return (
                  <article className="workflow-item" key={step.title}>
                    <span className="workflow-icon" aria-hidden="true">
                      <Icon size={19} strokeWidth={2} />
                    </span>
                    <div>
                      <h3>{step.title}</h3>
                      <p>{step.copy}</p>
                    </div>
                  </article>
                );
              })}
            </div>
            <div className="signal-panel" aria-label="Publishing overview preview">
              {metrics.map(([value, label]) => (
                <div className="metric" key={label}>
                  <strong>{value}</strong>
                  <span>{label}</span>
                </div>
              ))}
              <div className="signal-chart" aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        </section>

        <section className="section workflow" id="workflow" aria-labelledby="workflow-title">
          <div className="section-heading narrow">
            <div className="section-kicker">Workflow</div>
            <h2 id="workflow-title">From idea to insight</h2>
            <p>
              Draft once, adapt by channel, route approvals, and watch the signal return without breaking the publishing flow.
            </p>
          </div>
          <div className="workflow-grid">
            <div className="workflow-rail">
              <span>Idea</span>
              <span>Draft</span>
              <span>Approve</span>
              <span>Publish</span>
              <span>Learn</span>
            </div>
            <div className="insight-board">
              <div className="insight-header">
                <span>Launch sequence</span>
                <span>Live readiness</span>
              </div>
              <div className="insight-row active">
                <MessageSquareText size={18} />
                <div>
                  <strong>Thread and carousel are aligned</strong>
                  <span>Copy adapted for creator, company, and community channels.</span>
                </div>
                <Check size={18} />
              </div>
              <div className="insight-row">
                <Clock3 size={18} />
                <div>
                  <strong>Approval window closes at 4:30 PM</strong>
                  <span>Two stakeholders left to review the final caption set.</span>
                </div>
                <CircleDot size={18} />
              </div>
              <div className="insight-row">
                <Sparkles size={18} />
                <div>
                  <strong>Best next slot found</strong>
                  <span>Move the product update forward by 45 minutes.</span>
                </div>
                <Check size={18} />
              </div>
            </div>
          </div>
        </section>

        <section className="access" id="waitlist" aria-labelledby="waitlist-title">
          <div className="access-inner">
            <div>
              <div className="section-kicker">Early access</div>
              <h2 id="waitlist-title">Be first in when we open</h2>
              <p>
                PostUnite is not open for signup yet. Leave an email and we will send an invite as soon as workspaces are available. Questions go to contact@otraasi.com.
              </p>
            </div>
            <WaitlistForm />
          </div>
        </section>
      </main>
  );
}


function HeroActions() {
  return (
    <div className="hero-actions">
      <a className="button primary" href="/#waitlist">
        Join the waitlist
        <ArrowRight size={16} strokeWidth={2.2} />
      </a>
      <a className="button secondary" href="/#workflow">
        See workflow
        <ArrowDown size={16} strokeWidth={2.2} />
      </a>
    </div>
  );
}

function WaitlistForm() {
  const ref = useAffiliateRef();
  const [email, setEmail] = React.useState("");
  const [status, setStatus] = React.useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = React.useState("");

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "loading") {
      return;
    }

    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, source: ref ? `homepage:${ref}` : "homepage" })
      });
      const data = (await response.json()) as { ok?: boolean; error?: string };

      if (!response.ok || !data.ok) {
        setStatus("error");
        setMessage(data.error || "Something went wrong. Try again in a moment.");
        return;
      }

      setStatus("success");
      setEmail("");
      setMessage("You are on the list. We will email you when workspaces open.");
    } catch {
      setStatus("error");
      setMessage("Network error. Try again in a moment.");
    }
  };

  return (
    <form className="waitlist-form" onSubmit={onSubmit} noValidate>
      <label htmlFor="waitlist-email">Email address</label>
      <div className="form-row">
        <span aria-hidden="true">
          <Mail size={18} strokeWidth={2} />
        </span>
        <input
          id="waitlist-email"
          type="email"
          name="email"
          autoComplete="email"
          placeholder="you@company.com"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <button type="submit" disabled={status === "loading"}>
          {status === "loading" ? "Sending" : "Join"}
        </button>
      </div>
      <p
        className={`form-message${status === "success" ? " success" : ""}${status === "error" ? " error" : ""}`}
        role="status"
        aria-live="polite"
      >
        {message ||
          (ref
            ? "This invite includes 20% off Standard for six months when we open."
            : "No spam. One email when your workspace is ready.")}
      </p>
    </form>
  );
}

function FooterWaitlistLink() {
  return <a href="/#waitlist">Waitlist</a>;
}

function AffiliatesPage() {
  const ref = useAffiliateRef();
  return (
    <main className="legal-main">
      <section className="legal-hero" aria-labelledby="affiliates-title">
        <span className="legal-icon" aria-hidden="true">
          <Gift size={24} strokeWidth={2} />
        </span>
        <p className="section-kicker">Affiliate program</p>
        <h1 id="affiliates-title">Share PostUnite. Earn $10 a month for 3 months.</h1>
        <p>
          Give teams 20% off Standard for six months. After they stay paying for three months, you earn $10/month for three months — $30 max.
        </p>
        <div className="hero-actions" style={{ marginTop: 24 }}>
          <a className="button primary" href={APP_AFFILIATES}>
            Get your link
            <Link2 size={16} strokeWidth={2.2} />
          </a>
          <a className="button secondary" href={authUrl(ref)}>
            Create account
            <Send size={16} strokeWidth={2.2} />
          </a>
        </div>
      </section>
      <section className="affiliate-grid" aria-label="Affiliate program details">
        <article className="affiliate-card">
          <span className="workflow-icon" aria-hidden="true">
            <BadgeDollarSign size={19} strokeWidth={2} />
          </span>
          <h2>For people you refer</h2>
          <p>
            Referred workspaces get 20% off the $30/mo STANDARD plan for the first 6 months — $24/mo — then the regular $30/mo rate. The discount is applied at checkout.
          </p>
        </article>
        <article className="affiliate-card">
          <span className="workflow-icon" aria-hidden="true">
            <Gift size={19} strokeWidth={2} />
          </span>
          <h2>For you</h2>
          <p>
            You earn $10/month for 3 months after the referred workspace stays an active paying subscriber for at least 3 months. Payouts go through Stripe Connect. Nothing is marked paid until a real transfer succeeds.
          </p>
        </article>
        <article className="affiliate-card affiliate-card-wide">
          <h2>How it works</h2>
          <ol className="affiliate-steps">
            <li>Create a free PostUnite account and copy your unique link from the Affiliates page.</li>
            <li>Share <code>https://postunite.com/?ref=CODE</code>. Visitors are sent to signup at app.postunite.com with the same code.</li>
            <li>When they subscribe to Standard monthly, they get 20% off for 6 months.</li>
            <li>After their 3rd successful paid invoice, we start $10 transfers for 3 months (max $30).</li>
          </ol>
        </article>
      </section>
    </main>
  );
}

function PrivacyPage() {
  return (
    <LegalPage
      icon={<ShieldCheck size={24} strokeWidth={2} />}
      title="Privacy Policy"
      intro="This Privacy Policy explains how PostUnite collects, uses, and protects information when you use our website and product."
      sections={[
        {
          title: "Information we collect",
          body: [
            "If you join an email list or contact us, we collect the email address you submit and a short source label so we can understand where the request came from. If you create an account, we collect the information needed to operate your workspace.",
            "We may automatically receive limited technical information such as browser type, user agent, approximate request time, and basic server logs needed to operate, secure, and debug the website."
          ]
        },
        {
          title: "How we use information",
          body: [
            "We use contact and account information to operate PostUnite, send service-related messages, and respond to support requests.",
            "We use technical information to maintain site reliability, prevent abuse, improve the experience, and understand aggregate interest in the product."
          ]
        },
        {
          title: "Sharing and service providers",
          body: [
            "We do not sell personal information. We may share information with infrastructure and operational providers that help us host the website, run the product, send messages, or protect the service.",
            "Those providers are expected to process information only as needed to provide services to PostUnite."
          ]
        },
        {
          title: "Retention",
          body: [
            "We keep account and contact information for as long as needed to provide the service and related communications, unless you ask us to delete it earlier.",
            "Operational logs are kept only for a reasonable period needed for security, diagnostics, and compliance."
          ]
        },
        {
          title: "Your choices",
          body: [
            "You can ask to access, correct, or delete your information. You can also opt out of marketing messages by using the unsubscribe option in an email or contacting us directly.",
            "Some operational messages may still be sent when necessary to respond to your requests or manage access you asked for."
          ]
        },
        {
          title: "Security",
          body: [
            "We use reasonable technical and organizational safeguards designed to protect information. No internet service can guarantee absolute security, but we work to limit collection and access."
          ]
        },
        {
          title: "Children",
          body: [
            "PostUnite is not intended for children under 13, and we do not knowingly collect personal information from children."
          ]
        },
        {
          title: "Changes",
          body: [
            "We may update this Privacy Policy as PostUnite develops. The updated date on this page will show when changes were last made."
          ]
        },
        {
          title: "Contact",
          body: ["For privacy requests or questions, contact us at privacy@postunite.com. If that mailbox is unavailable, use contact@otraasi.com."]
        }
      ]}
    />
  );
}

function TermsPage() {
  return (
    <LegalPage
      icon={<FileText size={24} strokeWidth={2} />}
      title="Terms of Service"
      intro="These Terms of Service govern your access to the PostUnite website, product, and related materials."
      sections={[
        {
          title: "Using PostUnite",
          body: [
            "You may use this website and the PostUnite product only for lawful purposes and in a way that does not interfere with the website, its infrastructure, or other users.",
            "Creating an account does not guarantee a specific pricing, feature availability, or continued availability of any preview or beta capability."
          ]
        },
        {
          title: "Accounts and early access",
          body: [
            "If we invite you to an early-access version of PostUnite, you are responsible for keeping account credentials confidential and for activity under your account.",
            "We may limit, suspend, or revoke access if we believe use is unsafe, unlawful, abusive, or inconsistent with these Terms."
          ]
        },
        {
          title: "Acceptable use",
          body: [
            "Do not use PostUnite to send spam, violate platform policies, infringe intellectual property rights, distribute malware, harass others, or process content you do not have rights to use.",
            "Do not attempt to reverse engineer, scrape, overload, bypass access controls, or test the security of PostUnite without written permission."
          ]
        },
        {
          title: "Content and permissions",
          body: [
            "You retain ownership of content you submit or connect to PostUnite. You grant us the permissions needed to operate, display, process, transmit, and improve the service for you.",
            "You are responsible for the accuracy, legality, and rights clearance of content, accounts, channels, and data you connect or manage through PostUnite."
          ]
        },
        {
          title: "Third-party services",
          body: [
            "PostUnite may integrate with social networks, analytics tools, or other third-party services. Those services are governed by their own terms and privacy policies.",
            "We are not responsible for third-party service availability, policy changes, account decisions, or content moderation actions."
          ]
        },
        {
          title: "Intellectual property",
          body: [
            "PostUnite, including its website, product design, software, branding, and related materials, is owned by PostUnite or its licensors and is protected by applicable laws.",
            "You may not copy, modify, distribute, or create derivative works from PostUnite materials except as expressly allowed by us."
          ]
        },
        {
          title: "Disclaimers",
          body: [
            "The website, product, and any preview materials are provided on an as-is and as-available basis. We do not promise uninterrupted availability, error-free operation, or that any preview feature will become generally available.",
            "To the fullest extent permitted by law, we disclaim implied warranties including merchantability, fitness for a particular purpose, and non-infringement."
          ]
        },
        {
          title: "Limitation of liability",
          body: [
            "To the fullest extent permitted by law, PostUnite will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for lost profits, data, goodwill, or business opportunities.",
            "Our total liability for claims related to the website, product, or preview access will be limited to the greater of the amount you paid us for the relevant service or 100 USD."
          ]
        },
        {
          title: "Changes and termination",
          body: [
            "We may update these Terms as the product develops. Continued use after changes become effective means you accept the updated Terms.",
            "We may modify, suspend, or discontinue the website, product, or preview access at any time."
          ]
        },
        {
          title: "Contact",
          body: ["For questions about these Terms, contact us at legal@postunite.com. If that mailbox is unavailable, use contact@otraasi.com."]
        }
      ]}
    />
  );
}

type LegalPageProps = {
  icon: React.ReactNode;
  title: string;
  intro: string;
  sections: Array<{ title: string; body: string[] }>;
};

function LegalPage({ icon, title, intro, sections }: LegalPageProps) {
  return (
    <main className="legal-main">
      <section className="legal-hero" aria-labelledby="legal-title">
        <span className="legal-icon" aria-hidden="true">
          {icon}
        </span>
        <p className="section-kicker">PostUnite</p>
        <h1 id="legal-title">{title}</h1>
        <p>{intro}</p>
        <span className="legal-updated">Last updated {legalUpdated}</span>
      </section>
      <section className="legal-content" aria-label={`${title} details`}>
        {sections.map((section) => (
          <article className="legal-section" key={section.title}>
            <h2>{section.title}</h2>
            {section.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </article>
        ))}
      </section>
    </main>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <a className="brand footer-brand" href="/" aria-label="PostUnite home">
        <span className="brand-mark" aria-hidden="true">
          <Layers3 size={18} strokeWidth={2.25} />
        </span>
        PostUnite
      </a>
      <div className="footer-links" aria-label="Footer navigation">
        <a href="/privacy">Privacy</a>
        <a href="/terms">Terms</a>
        <a href="/affiliates">Affiliates</a>
        <a href="mailto:contact@otraasi.com">Support</a>
        <FooterWaitlistLink />
      </div>
    </footer>
  );
}

function FunnelStage() {
  return (
    <div
      className="funnel reveal-delayed"
      role="img"
      aria-label="One draft post enters PostUnite and leaves as five channel-ready posts scheduled across LinkedIn, X, Instagram, Facebook, and YouTube."
    >
      <div className="funnel-col funnel-in">
        <p className="stage-label">
          <span className="stage-index">01</span> One draft
        </p>
        <article className="post-card source-card">
          <header className="post-card-head">
            <span className="post-avatar" aria-hidden="true">
              <PenLine size={14} strokeWidth={2} />
            </span>
            <div>
              <strong>Launch announcement</strong>
              <span>Draft · 940 characters · 1 image</span>
            </div>
          </header>
          <div className="post-lines" aria-hidden="true">
            <i />
            <i />
            <i />
            <i />
            <i />
          </div>
          <div className="post-media" aria-hidden="true">
            <ImageIcon size={16} strokeWidth={1.8} />
          </div>
          <footer className="post-card-foot">
            <span className="post-chip">
              <AtSign size={12} strokeWidth={2} />
              @postunite
            </span>
            <span className="post-chip">1 asset</span>
          </footer>
        </article>
      </div>

      <FunnelBridge className="bridge bridge-in" targets={[50]} />

      <div className="funnel-col funnel-core">
        <p className="stage-label">
          <span className="stage-index">02</span> PostUnite
        </p>
        <div className="core-node">
          <span className="core-mark" aria-hidden="true">
            <Layers3 size={22} strokeWidth={2} />
          </span>
          <strong>Adapt &amp; schedule</strong>
          <ul className="core-ops">
            {funnelOps.map((op) => {
              const Icon = op.icon;
              return (
                <li key={op.label}>
                  <Icon size={13} strokeWidth={2} />
                  {op.label}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <FunnelBridge className="bridge bridge-out" targets={[8, 29, 50, 71, 92]} />

      <div className="funnel-col funnel-out">
        <p className="stage-label">
          <span className="stage-index">03</span> Five posts, queued
        </p>
        <div className="output-stack">
          {funnelOutputs.map((output, index) => {
            const Icon = output.icon;
            return (
              <article
                className="post-card output-card"
                key={output.channel}
                style={{ ["--i" as string]: index }}
              >
                <span className="output-icon" aria-hidden="true">
                  <Icon size={15} strokeWidth={2} />
                </span>
                <div className="output-body">
                  <strong>{output.channel}</strong>
                  <span>{output.detail}</span>
                </div>
                <span className="output-time">
                  <Check size={11} strokeWidth={2.6} />
                  {output.time}
                </span>
              </article>
            );
          })}
        </div>
      </div>

      <p className="funnel-caption">
        One draft <ArrowRight size={13} strokeWidth={2.2} /> five channel-native posts, scheduled in a single pass.
      </p>
    </div>
  );
}

type FunnelBridgeProps = {
  className: string;
  targets: number[];
};

function FunnelBridge({ className, targets }: FunnelBridgeProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      {targets.map((y, index) => {
        const d = `M0,50 C42,50 58,${y} 100,${y}`;
        return (
          <g key={y}>
            <path className="bridge-track" d={d} vectorEffect="non-scaling-stroke" />
            <path
              className="bridge-flow"
              d={d}
              vectorEffect="non-scaling-stroke"
              style={{ animationDelay: `${index * 0.42}s` }}
            />
          </g>
        );
      })}
    </svg>
  );
}

export default App;

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
